import { useContext, useState, Fragment, useRef, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MobXProviderContext } from "mobx-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import {
  useMealNextAvailableDate,
  getValidMealList,
} from "utils/hooks/useMealNextAvailableDate";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Calendar } from "react-multi-date-picker";
import object from "utils/helpers/object";
import { warningYellow } from "assets/img";
import classNames from "classnames";

function BulkUserRemoveMealPlan({ visible, setModal, bulkUsers, meals }) {
  const { mealManagementStore } = useContext(MobXProviderContext);
  const { userStore } = useContext(MobXProviderContext);
  const { locationID } = userStore;
  const mealPlan = mealManagementStore.mealPlan;

  const upcomingOrderStatuses = mealManagementStore.upcomingOrderStatuses;

  const [warning, setWarning] = useState(false);

  const nextAvailableMeal = useMealNextAvailableDate(meals);

  const validMeals = getValidMealList(meals);

  const [changeEffect, setChangeEffect] = useState("next");

  const [selectedCustomDate, setSelectedCustomDate] = useState(null);

  const calendarRef = useRef(null);

  const [forceDeleteOrders, setForceDeleteOrders] = useState(false);

  const [stage, setStage] = useState("initial");

  const hasOneTeamCreationSource = bulkUsers?.some(
    (item) => item?.creation_source == "team_assignment"
  );

  const hasOneIndividualCreationSource = bulkUsers?.some(
    (item) => item?.creation_source != "team_assignment"
  );

  const hasOneOrder = upcomingOrderStatuses?.some(
    (item) => item?.has_one_order
  );

  useEffect(() => {
    if (visible && hasOneTeamCreationSource) {
      setStage("team-warning");
    } else if (visible && hasOneOrder) {
      setStage("cancel-orders");
    }
  }, [visible, hasOneTeamCreationSource]);

  useEffect(() => {
    if (visible) {
      let params = {
        location_id: locationID,
        ids: bulkUsers?.map((user) => user?.id),
      };
      mealManagementStore.getUpcomingOrderStatuses(params);
    }
  }, []);

  const handleGotIt = () => {
    if (hasOneOrder && hasOneIndividualCreationSource) {
      setStage("cancel-orders");
    } else if (hasOneIndividualCreationSource) {
      setStage("standard-confirm");
    } else {
      setModal(false);
    }
  };

  const onDeletePress = () => {
    let payload = {
      location_id: locationID,
      ids: bulkUsers
        ?.filter((user) => user?.creation_source == "individual_assignment")
        ?.map((user) => user?.id),
      meal_series_id: mealPlan?.id,
    };

    if (object.isPresent(nextAvailableMeal)) {
      payload.effective_date =
        changeEffect == "custom"
          ? selectedCustomDate ?? nextAvailableMeal?.mealDate
          : nextAvailableMeal?.mealDate;
    }

    if (forceDeleteOrders) payload.force_delete = true;

    mealManagementStore
      .deleteEmployeeMealPlan(payload, mealPlan?.id)
      .then(() => setModal(false));
  };

  const handleDateChange = (value) => {
    const selectedDate = dayjs(value.toDate()).format("YYYY-MM-DD");

    setSelectedCustomDate(selectedDate);
  };

  const onSavePress = () => {
    if (!warning && object.isPresent(nextAvailableMeal)) {
      setWarning(true);
    }
  };

  const onCancelFutureOrders = () => {
    if (object.isPresent(nextAvailableMeal)) {
      setWarning(true);
      setForceDeleteOrders(true);
    }
  };

  return (
    <Transition appear show={visible ? true : false} as={Fragment}>
      <Dialog as="div" className="relative z-[90]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {warning ? (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-col">
                    <div className="flex justify-between items-center mb-[22px]">
                      <button
                        className="font-inter-regular text-[16px] text-hh-accent-dark"
                        onClick={() => {
                          setWarning(false);
                          setSelectedCustomDate(null);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          size="xs"
                          className="mr-2"
                          style={{
                            color: "#130f26",
                          }}
                        />
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={() => setModal(false)}
                        className="btn-style"
                      >
                        <FontAwesomeIcon
                          style={{ width: 16, height: 26 }}
                          icon={faXmark}
                          size="2x"
                          color="#BBC0C5"
                        />
                      </button>
                    </div>

                    <h2 className="text-2xl font-inter-bold text-dark-gray">
                      {bulkUsers?.length > 1
                        ? `Set Date to Remove ${bulkUsers?.length} Users from ${mealPlan?.attributes?.name}`
                        : `Set Date to Remove User from ${mealPlan?.attributes?.name}`}
                    </h2>

                    <small className="font-inter-regular text-dark-gray text-[16px] mt-[16px] bg-light-gray py-[12px] px-[8px] rounded-lg">
                      These changes will apply to all future meals in the
                      series.
                    </small>

                    <strong className="text-dark-gray font-inter-semibold text-[16px] my-[16px]">
                      When should they take effect?
                    </strong>

                    <div className="d-col rounded-lg">
                      <div key="next" className="flex items-center">
                        <input
                          name="date-method"
                          type="radio"
                          value="next"
                          checked={changeEffect == "next"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="photo"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Next available meal in series:{" "}
                          {nextAvailableMeal?.mealDate}
                        </label>
                      </div>

                      <div key="custom" className="flex items-center mt-[16px]">
                        <input
                          name="date-method"
                          type="radio"
                          value={"custom"}
                          checked={changeEffect == "custom"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="signature"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Pick a custom date
                        </label>
                      </div>
                    </div>

                    {changeEffect == "custom" && (
                      <div
                        className="flex flex-row justify-center items-center mt-[16px]"
                        ref={calendarRef}
                      >
                        <Calendar
                          highlightToday={true}
                          className="shadow-lg"
                          isMulti={false}
                          onChange={handleDateChange}
                          value={selectedCustomDate}
                          disableYearPicker={true}
                          monthYearSeparator={" "}
                          mapDays={({ date }) => {
                            const allowedDates = validMeals?.map(
                              (meal) => meal?.meal_date
                            );
                            const formatted = dayjs(date.toDate()).format(
                              "YYYY-MM-DD"
                            );
                            const isEnabled = allowedDates.includes(formatted);

                            return {
                              disabled: !isEnabled,
                              style: isEnabled
                                ? { color: "#000" }
                                : {
                                    color: "#ccc",
                                    textDecoration: "",
                                    pointerEvents: "none",
                                  },
                            };
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center items-center mt-5">
                    <button
                      className="btn-outline btn-style w-full"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className={classNames({
                        "btn-purple btn-style px-6 ml-3 w-full": true,
                        "btn-disabled":
                          changeEffect === "custom" && !selectedCustomDate,
                      })}
                      onClick={onDeletePress}
                      disabled={changeEffect == "custom" && !selectedCustomDate}
                    >
                      Remove User
                    </button>
                  </div>
                </Dialog.Panel>
              ) : (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all">
                  {stage == "team-warning" ? (
                    <div>
                      <div className="d-col">
                        <div className="d-row justify-between items-center">
                          <div />
                          <button
                            onClick={() => setModal(false)}
                            className="text-dark-gray hover:text-yellow-600"
                            aria-label="Close"
                          >
                            <span className="text-xl">&times;</span>
                          </button>
                        </div>

                        <img
                          src={warningYellow}
                          className="w-[40px] h-[40px] self-center mb-[8px]"
                        />

                        <h1 className="self-center text-center font-inter-semibold max-w-[546px] text-[24px] text-dark-gray">
                          Users Part of a Team
                        </h1>

                        <small className="text-dark-gray text-[16px] font-inter-regular mt-[16px]">
                          One or more of the selected users belong to a team
                          assigned to this meal. To remove them, you must either
                          remove the users from the team in the Uncatering: Team
                          section or remove the entire team from the meal plan.
                        </small>

                        <div className="d-row w-full items-center mt-[32px] justify-center">
                          <button
                            className="btn-purple w-full btn-style py-3 px-6"
                            onClick={handleGotIt}
                          >
                            Got It
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : stage == "cancel-orders" ? (
                    <div>
                      <div className="d-col">
                        <div className="d-row justify-between items-center">
                          <div />
                          <button
                            onClick={() => setModal(false)}
                            className="text-dark-gray hover:text-yellow-600"
                            aria-label="Close"
                          >
                            <span className="text-xl">&times;</span>
                          </button>
                        </div>

                        <img
                          src={warningYellow}
                          className="w-[40px] h-[40px] self-center mb-[8px]"
                        />

                        <h1 className="self-center text-center font-inter-semibold max-w-[546px] text-[24px] text-dark-gray">
                          Remove User from {mealPlan?.attributes?.name} and
                          Cancel Orders?
                        </h1>

                        <small className="text-dark-gray text-[16px] font-inter-regular mt-[16px]">
                          Currently, the user selected has existing orders
                          scheduled. Removing them now will cancel future
                          orders. This is not reversible. Do you still want to
                          proceed?
                        </small>

                        <span className="font-inter-regular text-[16px] text-secondary mt-[8px] self-center">
                          This action only removes the user from the meal plan.
                          To fully delete a user from your account, go to the
                          Uncatering: Teams section.
                        </span>

                        <div className="d-row w-full items-center mt-[24px] justify-center">
                          <button
                            className="btn-light w-full border-dark-gray btn-style py-3 px-5"
                            onClick={() => setModal(false)}
                          >
                            Cancel
                          </button>

                          <button
                            className="btn-purple w-full btn-style py-3 px-6 ml-3"
                            onClick={onCancelFutureOrders}
                          >
                            Continue
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="d-col">
                      <div className="d-row justify-between items-center">
                        <div />
                        <button
                          onClick={() => setModal(false)}
                          className="text-dark-gray hover:text-yellow-600"
                          aria-label="Close"
                        >
                          <span className="text-xl">&times;</span>
                        </button>
                      </div>

                      <h1 className="self-left font-inter-semibold text-[24px] text-dark-gray">
                        {bulkUsers?.length > 1
                          ? `Remove ${bulkUsers?.length} users from meal plan?`
                          : "Remove user from meal plan?"}
                      </h1>
                      <span className="font-inter-regular text-[16px] text-left text-dark-gray self-center">
                        This action only removes users from the meal plan. To
                        fully delete a user from your account, go to the
                        Uncatering: Teams section.
                      </span>

                      <div className="d-row w-full items-center mt-[24px] justify-center">
                        <button
                          className="btn-light w-full btn-style py-3 px-5"
                          onClick={() => setModal(false)}
                        >
                          Cancel
                        </button>

                        <button
                          className="btn-purple w-full btn-style py-3 px-6 ml-3"
                          onClick={onSavePress}
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              )}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default BulkUserRemoveMealPlan;
